import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import type { EmployeeIdentity } from '../../services/employees/employees.service';
import { EMPLOYEE_SESSION_EXPIRED_EVENT, EMPLOYEE_SESSION_LOOP_PROBE_EVENT } from '../../services/index/index.service';
import { reportError } from '../../services/logs/logs.service';
import { employeeSessionFingerprint, logEmployeeSessionAudit } from '../../services/employee-session-audit/employee-session-audit.service';
import { hardRefresh } from '../../utils/hard-refresh';

type EmployeeSessionState = {
  employee: EmployeeIdentity;
  employeeSession: string;
  activeSessionId: string;
};

type EmployeeSessionContextType = {
  employeeState: EmployeeSessionState | null;
  setEmployeeState: (tenantId: string, value: EmployeeSessionState) => void;
  clearEmployeeState: (tenantId: string) => void;
  hydrateEmployeeState: (tenantId: string) => EmployeeSessionState | null;
};

const EmployeeSessionContext = createContext<EmployeeSessionContextType | undefined>(undefined);

const buildStorageKey = (tenantId: string) => `posEmployeeSession:${tenantId}`;
const EMPLOYEE_SESSION_RESET_BURST_KEY = 'employee-session-reset-burst-v1';
const EMPLOYEE_SESSION_RESET_BURST_WINDOW_MS = 2 * 60 * 1000;
const EMPLOYEE_SESSION_RESET_BURST_LIMIT = 50;

type EmployeeSessionResetBurst = {
  count: number;
  lastAt: number;
};

const readResetBurst = (): EmployeeSessionResetBurst | null => {
  try {
    const raw = sessionStorage.getItem(EMPLOYEE_SESSION_RESET_BURST_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as EmployeeSessionResetBurst;
    if (
      typeof parsed?.count === 'number'
      && Number.isFinite(parsed.count)
      && typeof parsed?.lastAt === 'number'
      && Number.isFinite(parsed.lastAt)
    ) {
      return parsed;
    }
  } catch {
    // Best effort only: invalid sessionStorage state should never block recovery.
  }
  return null;
};

const writeResetBurst = (value: EmployeeSessionResetBurst): void => {
  try {
    sessionStorage.setItem(EMPLOYEE_SESSION_RESET_BURST_KEY, JSON.stringify(value));
  } catch {
    // Best effort only: recovery must still proceed when sessionStorage is unavailable.
  }
};

const clearResetBurst = (): void => {
  try {
    sessionStorage.removeItem(EMPLOYEE_SESSION_RESET_BURST_KEY);
  } catch {
    // Best effort only.
  }
};

const bumpResetBurst = (nowMs: number): number => {
  const current = readResetBurst();
  const withinWindow = current != null && nowMs - current.lastAt <= EMPLOYEE_SESSION_RESET_BURST_WINDOW_MS;
  const nextCount = withinWindow ? current.count + 1 : 1;
  writeResetBurst({ count: nextCount, lastAt: nowMs });
  return nextCount;
};

// Drops only the persisted session; callers that reload immediately need nothing else cleared.
export const clearEmployeeSessionStorage = (tenantId: string): void => {
  try {
    localStorage.removeItem(buildStorageKey(tenantId));
  } catch {
    // localStorage may be unavailable (private mode); never block the caller's reload on it.
  }
};

const isSameEmployeeSessionState = (
  left: EmployeeSessionState | null,
  right: EmployeeSessionState,
): boolean => {
  if (!left) {
    return false;
  }

  return (
    left.employeeSession === right.employeeSession &&
    left.employee.id === right.employee.id &&
    left.employee.tenantId === right.employee.tenantId &&
    left.employee.role === right.employee.role &&
    left.employee.fullName === right.employee.fullName &&
    left.employee.isActive === right.employee.isActive
  );
};

export const EmployeeSessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [employeeStateByTenant, setEmployeeStateByTenant] = useState<Record<string, EmployeeSessionState>>({});
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);

  // Summary: Clears all tenant-scoped POS employee sessions when backend marks any employee session as expired, ensuring UI reliably returns...
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  const clearAllEmployeeSessions = useCallback(() => {
    logEmployeeSessionAudit('provider_clear_all_sessions_started', {
      activeTenantId,
      tenantCount: Object.keys(employeeStateByTenant).length,
    });
    setEmployeeStateByTenant({});
    setActiveTenantId(null);

    const employeeSessionStorageKeys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith('posEmployeeSession:')) {
        employeeSessionStorageKeys.push(key);
      }
    }

    employeeSessionStorageKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
    logEmployeeSessionAudit('provider_clear_all_sessions_completed', {
      activeTenantId,
      clearedStorageCount: employeeSessionStorageKeys.length,
    });
  }, [activeTenantId, employeeStateByTenant]);

  // Summary: Hydrates employee POS session from tenant-scoped local storage key so POS shell can survive refresh without forcing immediate r...
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  const hydrateEmployeeState = useCallback((tenantId: string): EmployeeSessionState | null => {
    setActiveTenantId(tenantId);
    const raw = localStorage.getItem(buildStorageKey(tenantId));
    if (!raw) {
      logEmployeeSessionAudit('provider_hydrate_no_stored_session', { tenantId });
      setEmployeeStateByTenant((previous) => {
        if (!previous[tenantId]) {
          return previous;
        }

        const next = { ...previous };
        delete next[tenantId];
        return next;
      });
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as EmployeeSessionState;
      setEmployeeStateByTenant((previous) => {
        const current = previous[tenantId] ?? null;
        if (isSameEmployeeSessionState(current, parsed)) {
          return previous;
        }

        return {
          ...previous,
          [tenantId]: parsed,
        };
      });
      clearResetBurst();
      return parsed;
    } catch {
      reportError({
        level: 'warn',
        errorName: 'EmployeeSessionStorageError',
        message: 'Employee session cleared because local storage could not be parsed',
        context: { reason: 'invalid-local-session-storage', tenantId },
      });
      logEmployeeSessionAudit('provider_hydrate_invalid_storage_cleared', { tenantId });
      localStorage.removeItem(buildStorageKey(tenantId));
      setEmployeeStateByTenant((previous) => {
        if (!previous[tenantId]) {
          return previous;
        }

        const next = { ...previous };
        delete next[tenantId];
        return next;
      });
      return null;
    }
  }, []);

  // Summary: Persists tenant-scoped employee POS session after successful PIN identification for subsequent manager/employee tab authorization.
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  const setEmployeeState = useCallback((tenantId: string, value: EmployeeSessionState) => {
    logEmployeeSessionAudit('provider_session_persisted', {
      tenantId,
      employeeId: value.employee.id,
      activeSessionId: employeeSessionFingerprint(value.activeSessionId),
    });
    setActiveTenantId(tenantId);
    localStorage.setItem(buildStorageKey(tenantId), JSON.stringify(value));
    clearResetBurst();
    setEmployeeStateByTenant((previous) => {
      const current = previous[tenantId] ?? null;
      if (isSameEmployeeSessionState(current, value)) {
        return previous;
      }

      return {
        ...previous,
        [tenantId]: value,
      };
    });
  }, []);

  // Summary: Clears tenant-scoped employee POS session during logout or identity switch flows to prevent stale employee authorization contex...
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  const clearEmployeeState = useCallback((tenantId: string) => {
    logEmployeeSessionAudit('provider_clear_tenant_session_started', {
      tenantId,
      activeSessionId: employeeSessionFingerprint(employeeStateByTenant[tenantId]?.activeSessionId),
    });
    clearEmployeeSessionStorage(tenantId);
    setEmployeeStateByTenant((previous) => {
      if (!previous[tenantId]) {
        return previous;
      }

      const next = { ...previous };
      delete next[tenantId];
      return next;
    });
    setActiveTenantId((previous) => (previous === tenantId ? null : previous));
    logEmployeeSessionAudit('provider_clear_tenant_session_completed', { tenantId });
  }, [employeeStateByTenant]);

  // Summary: Subscribes to global transport-layer employee session expiration signals and resets provider state so all POS surfaces converge...
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const enforceLoopBoundaryIfNeeded = (detail: Record<string, unknown> | undefined): boolean => {
      const resetBurstCount = bumpResetBurst(Date.now());
      if (resetBurstCount < EMPLOYEE_SESSION_RESET_BURST_LIMIT) {
        return false;
      }

      logEmployeeSessionAudit('provider_picker_reset_loop_boundary_reached', {
        limit: EMPLOYEE_SESSION_RESET_BURST_LIMIT,
        resetBurstCount,
        reason: detail?.reason ?? 'employee-session-expired-event',
        statusCode: detail?.statusCode ?? null,
        responseMessage: detail?.message ?? null,
        requestUrl: detail?.requestUrl ?? null,
      });
      reportError({
        level: 'warn',
        errorName: 'EmployeeSessionResetLoopBoundaryReached',
        message: 'Employee session reset loop boundary reached; forcing hard refresh',
        context: {
          limit: EMPLOYEE_SESSION_RESET_BURST_LIMIT,
          resetBurstCount,
          reason: detail?.reason ?? 'employee-session-expired-event',
          statusCode: detail?.statusCode ?? null,
          responseMessage: detail?.message ?? null,
          requestUrl: detail?.requestUrl ?? null,
        },
      });
      clearAllEmployeeSessions();
      clearResetBurst();
      void hardRefresh();
      return true;
    };

    const handleSessionExpired = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : undefined;
      reportError({
        level: 'warn',
        errorName: 'EmployeeSessionExpired',
        message: 'Employee session cleared after a recognized authentication response',
        context: {
          reason: detail?.reason ?? 'employee-session-expired-event',
          statusCode: detail?.statusCode,
          responseMessage: detail?.message,
          requestUrl: detail?.requestUrl,
        },
      });
      logEmployeeSessionAudit('provider_received_picker_reset', {
        reason: detail?.reason ?? 'employee-session-expired-event',
        statusCode: detail?.statusCode ?? null,
        responseMessage: detail?.message ?? null,
        requestUrl: detail?.requestUrl ?? null,
      });
      if (enforceLoopBoundaryIfNeeded(detail)) {
        return;
      }
      clearAllEmployeeSessions();
    };

    const handleLoopProbe = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : undefined;
      logEmployeeSessionAudit('provider_received_picker_reset_loop_probe', {
        reason: detail?.reason ?? 'employee-session-loop-probe-event',
        statusCode: detail?.statusCode ?? null,
        responseMessage: detail?.message ?? null,
        requestUrl: detail?.requestUrl ?? null,
      });
      enforceLoopBoundaryIfNeeded(detail);
    };

    window.addEventListener(EMPLOYEE_SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener(EMPLOYEE_SESSION_LOOP_PROBE_EVENT, handleLoopProbe);
    return () => {
      window.removeEventListener(EMPLOYEE_SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener(EMPLOYEE_SESSION_LOOP_PROBE_EVENT, handleLoopProbe);
    };
  }, [clearAllEmployeeSessions]);

  const employeeState = activeTenantId ? employeeStateByTenant[activeTenantId] ?? null : null;

  const contextValue = useMemo(
    () => ({
      employeeState,
      setEmployeeState,
      clearEmployeeState,
      hydrateEmployeeState,
    }),
    [clearEmployeeState, employeeState, hydrateEmployeeState, setEmployeeState],
  );

  return (
    <EmployeeSessionContext.Provider value={contextValue}>
      {children}
    </EmployeeSessionContext.Provider>
  );
};

export const useEmployeeSession = () => {
  const context = useContext(EmployeeSessionContext);
  if (!context) {
    throw new Error('useEmployeeSession must be used within EmployeeSessionProvider');
  }

  return context;
};

